import { useGlobalNotifier, useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfCheckboxesField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfInputDatepickerField,
  FrVrfInputDaterangeField,
  FrVrfMultipleLineInputField,
  FrVrfRadioButtonField,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  InputProjectInformationDialogInitialState,
  Option,
  useInputProjectInformationDialogState,
} from ".";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import { useGetGeneralCodes } from "../../../services/data-list";
import {
  CreateProjectRequest,
  UpdateProjectRequest,
  useCreateProject,
  useUpdateProject,
} from "../../../services/project";
import { createModal } from "../../modal";
import { isProperUser } from "../../../util/auth-util";
import { useUserDetails } from "@vrf/fr-vrf-core-react";

enum UsageType {
  Use = "Use",
  DoNotUse = "DoNotUse",
}

const MANUAL_LINK = {
  tooltip: "1. Create Project",
  value:
    "https://confluence.fastretailing.com/pages/viewpage.action?pageId=793717328",
};

export const InputProjectInformationDialogContent: React.FC = () => {
  const navigate = useNavigate();
  const { userDetails } = useUserDetails();
  const { showToast, showConfirmationDialog, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [getGeneralCodesResponse, getGeneralCodesRequest] =
    useGetGeneralCodes();
  const [updateProjectResponse, updateProjectRequest] = useUpdateProject();
  const [createProjectResponse, createProjectRequest] = useCreateProject();
  const [pageState, setPageState] = useInputProjectInformationDialogState();
  const { moveToModalPage, closeModalPage, isModalPage } = usePageNavigation();

  const closeModal = (isSaved: boolean, isCreate: boolean) => {
    if (!isSaved) {
      resetUserError();
      setPageState(InputProjectInformationDialogInitialState);
      return;
    }
    closeModalPage();

    setPageState((prevState) => ({
      ...prevState,
      isShow: false,
      isCreate: isCreate,
      status: {
        ...prevState.status,
        isSaved: true,
      },
    }));
  };

  const onInitialDisplayed = (): void => {
    moveToModalPage();
    resetUserError();
    const cdTypes = ["in_def_common_flag_type", "chn_type_grp_type"];
    getGeneralCodesRequest({ cdTypes: cdTypes });
  };
  useEffect(onInitialDisplayed, []);

  const ongetGetGeneralCodes = () => {
    let trnsmsnType = [] as Option[];
    let chnTypeGrpType = [] as Option[];
    const generalCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];

    generalCode.forEach((e) => {
      switch (e.cdType) {
        case "in_def_common_flag_type":
          trnsmsnType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "chn_type_grp_type":
          chnTypeGrpType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
      }
    });

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        trnsmsnType: trnsmsnType,
        chnTypeGrpType: chnTypeGrpType,
      },
    }));
  };
  useEffect(ongetGetGeneralCodes, [getGeneralCodesResponse]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;

    if (name === "ediUsage") {
      setPageState((prevState) => ({
        ...prevState,
        edited: {
          ...prevState.edited,
          chnTypeGrpTypeCdList:
            value === UsageType.DoNotUse
              ? []
              : prevState.edited.chnTypeGrpTypeCdList,
        },
        status: {
          ...prevState.status,
          ediUsage: value,
          invalidList:
            value === UsageType.DoNotUse
              ? prevState.status.invalidList.filter((e) => e !== "ediPrtcl")
              : prevState.status.invalidList,
        },
      }));
    } else {
      setPageState((prevState) => ({
        ...prevState,
        edited: {
          ...prevState.edited,
          [name]: value,
        },
        status: {
          ...prevState.status,
          invalidList: isValid
            ? prevState.status.invalidList.filter((e) => e !== name)
            : prevState.status.invalidList.concat(name),
        },
      }));
    }
  };

  const onChangeDateRangeHandle = (name: string) => (event: CustomEvent) => {
    const isValid = event.detail.isValid;

    setPageState((prevState) => ({
      ...prevState,
      edited: {
        ...prevState.edited,
        pjtStartPlnDate: event.detail.startDate,
        pjtEndPlnDate: event.detail.endDate,
      },
      status: {
        ...prevState.status,
        invalidList: isValid
          ? prevState.status.invalidList.filter((e) => e !== name)
          : prevState.status.invalidList.concat(name),
      },
    }));
  };

  const onChangeDateHandle = (name: string) => (event: CustomEvent) => {
    const isValid = event.detail.isValid;

    setPageState((prevState) => ({
      ...prevState,
      edited: {
        ...prevState.edited,
        [name]: event.detail.date,
      },
      status: {
        ...prevState.status,
        invalidList: isValid
          ? prevState.status.invalidList.filter((e) => e !== name)
          : prevState.status.invalidList.concat(name),
      },
    }));
  };

  const onClickCancel = () => {
    if (JSON.stringify(pageState.saved) === JSON.stringify(pageState.edited)) {
      closeModal(false, false);
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          closeModal(false, false);
        },
      });
    }
  };

  const onClickSave = () => {
    const createProject = () => {
      resetUserError();
      const request: CreateProjectRequest = {
        projectInformation: { ...pageState.edited },
      };
      createProjectRequest(request);
    };

    const updateProject = () => {
      resetUserError();
      const request: UpdateProjectRequest = {
        pjtId: pageState.pjtId,
        projectInformation: { ...pageState.edited },
        updateDateTime: pageState.updateDateTime,
      };
      updateProjectRequest(request);
    };

    if (pageState.pjtId === "") {
      showConfirmationDialog({
        title: "",
        message: "",
        type: "confirm",
        onOkButtonClicked: createProject,
      });
    } else {
      showConfirmationDialog({
        title: "",
        message: "",
        type: "confirm",
        onOkButtonClicked: updateProject,
      });
    }
  };

  const onListenCreateProjectResponse = (): void => {
    if (!createProjectResponse) return;

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });
    navigate(`/project-plan?pjtId=${createProjectResponse.pjtId}`);
    closeModal(true, true);
  };
  useEffect(onListenCreateProjectResponse, [createProjectResponse]);

  const onListenUpdateProjectResponse = (): void => {
    if (!updateProjectResponse) return;

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });
    closeModal(true, false);
  };
  useEffect(onListenUpdateProjectResponse, [updateProjectResponse]);

  const getContent = () => {
    return (
      <>
        <div
          style={{
            paddingTop: "10px",
            paddingBottom: "30px",
            marginLeft: "30px",
          }}
        >
          <FrVrfFormPanel columns={1}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <FrVrfSingleLineInputField
                labelContent="Project Name"
                content={pageState.edited.pjtName}
                placeholder="Sample project"
                required
                onInputValueChanged={onChangeValueHandle("pjtName")}
                constraint="[IsNotBlank(value), Size(value, {max: 240})]"
                errorMessage={errorMessages(
                  pageState.fieldPath.pjtName,
                  isModalPage(),
                )}
              />
              <FrVrfButton
                icon="frIcon:question"
                theme="tertiary"
                size="small"
                tooltipVisible={true}
                tooltipPosition="below"
                tooltipContent={MANUAL_LINK.tooltip}
                style={{
                  "--vrf-tooltip-max-width": "120px",
                  marginRight: "50px",
                }}
                onClicked={() => {
                  window.open(MANUAL_LINK.value);
                }}
              />
            </div>
            <div>
              <FrVrfMultipleLineInputField
                labelContent="Project Description"
                content={pageState.edited.pjtDesc}
                placeholder="Description for sample project."
                resize="both"
                style={{
                  "--vrf-multiple-line-input-field-input-height": "100px",
                  "--vrf-multiple-line-input-field-input-width": "600px",
                }}
                required
                constraint="[IsNotBlank(value), Size(value, {max: 2000})]"
                onInputValueChanged={onChangeValueHandle("pjtDesc")}
                errorMessage={errorMessages(
                  pageState.fieldPath.pjtDesc,
                  isModalPage(),
                )}
              />
            </div>
            <div>
              <FrVrfInputDaterangeField
                labelContent="Scheduled Period"
                inputDateRange={{
                  startDate: pageState.edited.pjtStartPlnDate ?? "",
                  endDate: pageState.edited.pjtEndPlnDate ?? "",
                }}
                dateFormat="ymd"
                clearButtonVisible={false}
                required
                constraint="IsNotBlank(value)"
                onInputValueChanged={onChangeDateRangeHandle("period")}
                errorMessage={
                  errorMessages(
                    pageState.fieldPath.pjtStartPlnDate,
                    isModalPage(),
                  ) +
                  errorMessages(
                    pageState.fieldPath.pjtEndPlnDate,
                    isModalPage(),
                  )
                }
              />
            </div>
            <div>
              <FrVrfRadioButtonField
                labelContent="External Connection"
                optionItems={pageState.codes.trnsmsnType}
                checked={pageState.edited.trnsMsnTypeCd}
                required
                constraint="IsNotBlank(value)"
                onRadioCheckedChanged={onChangeValueHandle("trnsMsnTypeCd")}
                errorMessage={errorMessages(
                  pageState.fieldPath.trnsMsnTypeCd,
                  isModalPage(),
                )}
              />
            </div>
            <div>
              <div>
                <FrVrfRadioButtonField
                  labelContent="EDI Protocol"
                  optionItems={pageState.codes.usageType}
                  checked={pageState.status.ediUsage}
                  required
                  constraint="IsNotBlank(value)"
                  onRadioCheckedChanged={onChangeValueHandle("ediUsage")}
                  errorMessage={
                    pageState.status.ediUsage === ""
                      ? errorMessages(
                          pageState.fieldPath.chnTypeGrpTypeCdList,
                          isModalPage(),
                        )
                      : ""
                  }
                />
              </div>
              {pageState.status.ediUsage === UsageType.Use ? (
                <div>
                  <FrVrfCheckboxesField
                    labelContent=" "
                    options={pageState.codes.chnTypeGrpType}
                    checkList={pageState.edited.chnTypeGrpTypeCdList ?? []}
                    required
                    constraint="IsNotBlank(value)"
                    onCheckboxesChanged={onChangeValueHandle(
                      "chnTypeGrpTypeCdList",
                    )}
                    errorMessage={errorMessages(
                      pageState.fieldPath.chnTypeGrpTypeCdList,
                      isModalPage(),
                    )}
                  />
                </div>
              ) : null}
            </div>
            <div>
              <FrVrfInputDatepickerField
                labelContent="Due Date for IE Cost Estimation"
                defaultDate={pageState.edited.reqEstmtAnsDueDate}
                required
                constraint="IsNotBlank(value)"
                onInputValueChanged={onChangeDateHandle("reqEstmtAnsDueDate")}
                errorMessage={errorMessages(
                  pageState.fieldPath.reqEstmtAnsDueDate,
                  isModalPage(),
                )}
              />
            </div>
          </FrVrfFormPanel>
          <FrVrfFooter>
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancel}
            />
            <FrVrfButton
              content="Save"
              theme="primary"
              size="large"
              disabled={
                JSON.stringify(pageState.saved) ===
                  JSON.stringify(pageState.edited) ||
                pageState.status.invalidList.length !== 0
              }
              visible={isProperUser(userDetails)}
              onClicked={onClickSave}
            />
          </FrVrfFooter>
        </div>
      </>
    );
  };

  return createModal(
    "Project Information",
    getContent(),
    onClickCancel,
    "large",
  );
};
